import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Field } from 'react-final-form';
import PropTypes from 'prop-types';
import { Divider, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { ORGANIZATIONS } from 'api/graphql/organization';
import AutocompleteFieldAdapter from 'components/shared/field-adapters/AutocompleteFieldAdapter';
import DatePickerFieldAdapter from 'components/shared/field-adapters/DatePickerFieldAdapter';
import MultipleSelectFieldAdapter from 'components/shared/field-adapters/MultipleSelectFieldAdapter';
import PasswordFieldAdapter from 'components/shared/field-adapters/PasswordFieldAdapter';
import SelectFieldAdapter from 'components/shared/field-adapters/SelectFieldAdapter';
import TextAreaFieldAdapter from 'components/shared/field-adapters/TextAreaFieldAdapter';
import TextFieldAdapter from 'components/shared/field-adapters/TextFieldAdapter';
import { getGenderOptions, getPlatformOptions, getUiLanguageOptions } from 'common/constants/options';
import { buildPlanFieldOptions } from 'common/form/planPlatform';
import requestStatus from 'common/constants/status';
import { isRequiredField } from 'common/utils/general';

const DEFAULT_ACTIVE_PLANS = [];

function ClientFields({
  activePlans = DEFAULT_ACTIVE_PLANS,
  cities,
  countries,
  form,
  geoStatus,
  planOptions,
  planStatus,
  requiredFields,
  showOrganization = false,
  states,
  values
}) {
  const { t } = useTranslation(['labels', 'options']);

  // Organización del cliente (solo en edición admin). Permite reasignarlo "desde el perfil".
  const { data: orgData } = useQuery(ORGANIZATIONS, {
    variables: { first: 200, page: 1 },
    skip: !showOrganization,
    fetchPolicy: 'cache-and-network'
  });
  const organizationOptions = useMemo(
    () => (orgData?.organizations?.data ?? []).map((o) => ({ value: String(o.id), label: o.name })),
    [orgData]
  );

  // Plan y Plataforma son multi-select CHECKBOXEABLES (coexistencia): un cliente puede tener plan
  // primario + Active Plus + adicionales. Los adicionales (de sus pagos) se muestran solo-lectura con chip.
  const planFieldOptions = useMemo(
    () =>
      buildPlanFieldOptions(planOptions, activePlans, {
        activePlus: t('platform.activePlus', { ns: 'options', defaultValue: 'Active Plus' }),
        additional: t('additionalPlan', { ns: 'labels', defaultValue: 'Adicional' })
      }),
    [planOptions, activePlans, t]
  );

  return (
    <>
      <Grid container spacing={4}>
        <Grid xs={12} sm={4}>
          <Field
            id="first_name"
            name="first_name"
            component={TextFieldAdapter}
            label={t('firstname', { count: 2, ns: 'labels' })}
            type="text"
            fullWidth
            required={isRequiredField('first_name', requiredFields)}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="last_name"
            name="last_name"
            component={TextFieldAdapter}
            label={t('lastname', { count: 2, ns: 'labels' })}
            type="text"
            fullWidth
            required={isRequiredField('last_name', requiredFields)}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="gender"
            name="gender"
            component={SelectFieldAdapter}
            options={getGenderOptions(t)}
            label={t('gender', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="email"
            name="email"
            component={TextFieldAdapter}
            label={t('email', { ns: 'labels' })}
            type="text"
            autoComplete="email"
            fullWidth
            required={isRequiredField('email', requiredFields)}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="birthdate"
            name="birthdate"
            component={DatePickerFieldAdapter}
            label={t('birthdate', { ns: 'labels' })}
            renderInputProps={{
              fullWidth: true,
              required: isRequiredField('birthdate', requiredFields)
            }}
            disableFuture
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="phone"
            name="phone"
            component={TextFieldAdapter}
            label={t('phone', { ns: 'labels' })}
            type="text"
            fullWidth
            required={isRequiredField('phone', requiredFields)}
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="country"
            name="country"
            component={AutocompleteFieldAdapter}
            options={countries}
            renderInputProps={{
              label: t('country', { ns: 'labels' }),
              required: isRequiredField('country', requiredFields)
            }}
            loading={geoStatus === requestStatus.loading}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="state"
            name="state"
            component={AutocompleteFieldAdapter}
            disabled={!values.country}
            options={states}
            renderInputProps={{
              label: t('state', { ns: 'labels' }),
              required: isRequiredField('state', requiredFields)
            }}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="city"
            name="city"
            component={AutocompleteFieldAdapter}
            disabled={!values.country || !values.state}
            options={cities}
            renderInputProps={{
              label: t('city', { ns: 'labels' }),
              required: isRequiredField('city', requiredFields)
            }}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={4}>
          <Field
            id="ui_language"
            name="ui_language"
            component={SelectFieldAdapter}
            options={getUiLanguageOptions(t)}
            label={t('uiLanguage', { ns: 'labels' })}
            fullWidth
            required={isRequiredField('ui_language', requiredFields)}
          />
        </Grid>
      </Grid>
      <Typography
        component="h4"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(26),
          marginBottom: (theme) => theme.spacing(1),
          marginTop: '35px'
        }}>
        {t('planDetails', { ns: 'labels' })}
      </Typography>
      <Divider sx={{ marginBottom: '22px' }} />
      <Grid container spacing={4}>
        <Grid xs={12} sm={6}>
          <Field
            id="plans"
            name="plans"
            component={MultipleSelectFieldAdapter}
            options={planFieldOptions}
            disabled={planStatus === requestStatus.loading}
            label={t('plan', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={6}>
          <Field
            id="platforms"
            name="platforms"
            component={MultipleSelectFieldAdapter}
            options={getPlatformOptions(t)}
            label={t('platform', { ns: 'labels' })}
            fullWidth
          />
        </Grid>
        {showOrganization && (
          <Grid xs={12} sm={6}>
            <Field
              id="organization_id"
              name="organization_id"
              component={SelectFieldAdapter}
              options={organizationOptions}
              label={t('organization', { ns: 'labels', defaultValue: 'Organización' })}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      <Typography
        component="h4"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(26),
          marginBottom: (theme) => theme.spacing(1),
          marginTop: '35px'
        }}>
        {t('password', { ns: 'labels' })}
      </Typography>
      <Divider sx={{ marginBottom: '22px' }} />
      <Grid container spacing={4}>
        <Grid xs={12} sm={4}>
          <Field
            id="password"
            name="password"
            component={PasswordFieldAdapter}
            label={t('password', { ns: 'labels' })}
            autoComplete="current-password"
            onShuffle={form.mutators.generatePassword}
            fullWidth
            required={isRequiredField('password', requiredFields)}
            shuffle
          />
        </Grid>
      </Grid>
      <Typography
        component="h4"
        sx={{
          fontSize: (theme) => theme.typography.pxToRem(26),
          marginBottom: (theme) => theme.spacing(1),
          marginTop: '35px'
        }}>
        {t('observations', { ns: 'labels' })}
      </Typography>
      <Divider sx={{ marginBottom: '22px' }} />
      <Grid container>
        <Grid xs={12}>
          <Field
            id="observations"
            name="observations"
            component={TextAreaFieldAdapter}
            label={t('observations', { ns: 'labels' })}
            helperText={t('text', { ns: 'validations' })}
            multiline
            minRows={5}
            type="text"
            fullWidth
          />
        </Grid>
      </Grid>
    </>
  );
}

ClientFields.propTypes = {
  activePlans: PropTypes.array,
  cities: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  form: PropTypes.object.isRequired,
  geoStatus: PropTypes.string.isRequired,
  planOptions: PropTypes.array.isRequired,
  planStatus: PropTypes.string.isRequired,
  requiredFields: PropTypes.array.isRequired,
  showOrganization: PropTypes.bool,
  states: PropTypes.array.isRequired,
  values: PropTypes.object.isRequired
};

export default ClientFields;
